import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Switch from "@radix-ui/react-switch";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Bell,
  Search,
  MoreVertical,
  ArrowUpDown,
  CheckCheck,
  Circle,
  Check,
  BellOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  NotificationData,
  useCounterNotification,
  useCreateUserNotificationPreference,
  useNotifications,
  useReadAllNotification,
  useReadNotification,
  useUnreadNotification,
  useUpdateUserNotificationPreference,
  useUserNotificationPreference,
} from "@/api/notifications";
import { formatDateString, formatIsoAsWib } from "@/utils/helper/formatDate";
import { resolveActionUrl } from "@/utils/helper/resolveActionUrl";
import { useNotifRetention } from "@/api/master-param";
import { swalErrorDefault } from "@/utils/lib/swal";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useProfileByKeycloakId } from "@/api/auth";

type NotificationFilter = "all" | "todo" | "read" | "unread";

export default function NotificationDropdown() {
  // user profile
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });
  const userId = data?.id;

  const { data: notifications } = useNotifications({
    limit: "100",
    page: "0",
    asc: "false",
    sort: "createdDate",
  });
  const { data: notifRetention } = useNotifRetention();
  const { data: unreadCount } = useCounterNotification();
  const { data: userPreference } = useUserNotificationPreference({
    userId: userId ?? "",
  });

  const { mutate: read } = useReadNotification();
  const { mutate: unread } = useUnreadNotification();
  const { mutate: readAll } = useReadAllNotification();
  const { mutate: updateUserNotificationPreference } =
    useUpdateUserNotificationPreference();
  const { mutate: createPreference } = useCreateUserNotificationPreference();

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  const navigate = useNavigate();

  const filteredNotifications = notifications?.content
    ?.filter((n) => {
      if (filter === "todo") return n.notification.type === "TODO";
      if (filter === "read") return n.isRead;
      if (filter === "unread") return !n.isRead;
      return true;
    })
    .filter((n) => {
      if (!searchQuery) return true;
      return (
        n.notification.message
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        n.notification.type
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        n.notification.module
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        n.notification.statusData
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    })
    .sort((a, b) => {
      const timeA = new Date(a.notification.createdDate).getTime();
      const timeB = new Date(b.notification.createdDate).getTime();
      return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });

  // group by date and sort it from the new ones
  const groupedNotifications = filteredNotifications?.reduce(
    (acc, notif) => {
      const date = notif.notification.createdDate;
      // get the date only and make it sort from new ones
      const dateOnly = date.split("T")[0];

      if (!acc[dateOnly]) {
        acc[dateOnly] = [];
      }
      acc[dateOnly].push(notif);
      return acc;
    },
    {} as Record<string, NotificationData[]>,
  );

  const markAllAsRead = () => {
    readAll(undefined, {
      onError: () => {
        swalErrorDefault.fire();
      },
    });
  };

  const toggleNotificationStatus = (id: string, isRead: boolean) => {
    isRead
      ? unread(
          { id },
          {
            onError: () => {
              swalErrorDefault.fire();
            },
          },
        )
      : read(
          { id },
          {
            onError: () => {
              swalErrorDefault.fire();
            },
          },
        );
  };

  const handleUpdateUserNotificationPreference = ({
    isDoNotDisturb,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // isEmailEnabled,
  }: {
    isDoNotDisturb?: boolean;
    isEmailEnabled?: boolean;
  }) => {
    // if there is no user preference, create a new one
    if (userId && !userPreference) {
      createPreference({
        isDoNotDisturb: isDoNotDisturb ?? false,
        isEmailEnabled: false,
        user: {
          id: userId,
        },
      });
    }

    if (userId && userPreference) {
      updateUserNotificationPreference({
        id: userPreference?.id,
        isDoNotDisturb: isDoNotDisturb ?? userPreference?.isDoNotDisturb,
        isEmailEnabled: false,
        user: {
          id: userId,
        },
      });
    }
  };

  const isHaveNotification = notifications?.totalElements !== 0;
  const isDoNotDisturb = userPreference?.isDoNotDisturb;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className="relative h-min rounded-lg p-2 transition-colors data-[state=open]:bg-neutral-100">
          <Bell className="h-[1.2rem] w-[1.2rem] text-foreground" />
          {unreadCount !== undefined && unreadCount > 0 && !isDoNotDisturb && (
            <span className="absolute -top-0.5 right-0.5 flex h-[1.2rem] w-min items-center justify-center rounded-full bg-destructive px-1 text-xs text-destructive-foreground">
              {unreadCount}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="flex max-h-[680px] w-[480px] flex-col overflow-hidden rounded-xl border border-border bg-popover shadow-lg"
          sideOffset={8}
          align="end"
        >
          {/* Header */}
          <div className="border-b border-border px-5 py-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-medium text-foreground">Notifications</h3>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button className="rounded-lg p-1.5 transition-colors hover:bg-accent">
                    <MoreVertical className="h-4 w-4 text-muted-foreground" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="min-w-[180px] rounded-lg border border-border bg-popover p-1 shadow-lg"
                    sideOffset={4}
                  >
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none hover:bg-accent"
                      onClick={markAllAsRead}
                    >
                      <CheckCheck className="h-4 w-4" />
                      Mark all as read
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>

            {/* Filters */}
            <Tabs.Root
              value={filter}
              onValueChange={(v) => setFilter(v as NotificationFilter)}
            >
              <Tabs.List className="flex gap-1 rounded-lg bg-muted p-1">
                <Tabs.Trigger
                  value="all"
                  className="flex-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-all data-[state=active]:bg-axiata-blue data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  All
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="todo"
                  className="flex-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-all data-[state=active]:bg-axiata-blue data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  Todo
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="read"
                  className="flex-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-all data-[state=active]:bg-axiata-blue data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  Read
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="unread"
                  className="flex-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-all data-[state=active]:bg-axiata-blue data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  Unread
                </Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>

            {/* Settings */}
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="dnd"
                  className="cursor-pointer text-sm text-foreground"
                >
                  Do Not Disturb
                </label>
                <Switch.Root
                  id="dnd"
                  checked={userPreference?.isDoNotDisturb}
                  onCheckedChange={(checked) => {
                    handleUpdateUserNotificationPreference({
                      isDoNotDisturb: checked,
                    });
                  }}
                  className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors data-[state=checked]:bg-axiata-blue"
                >
                  <Switch.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 data-[state=checked]:translate-x-[22px]" />
                </Switch.Root>
              </div>

              {/* <div className="flex items-center justify-between">
                <label
                  htmlFor="email"
                  className="cursor-pointer text-sm text-foreground"
                >
                  Daily Email Notification
                </label>
                <Switch.Root
                  id="email"
                  checked={userPreference?.isEmailEnabled}
                  onCheckedChange={(checked) => {
                    handleUpdateUserNotificationPreference({
                      isEmailEnabled: checked,
                    });
                  }}
                  className="relative h-6 w-11 rounded-full bg-gray-300 transition-colors data-[state=checked]:bg-axiata-blue"
                >
                  <Switch.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 data-[state=checked]:translate-x-[22px]" />
                </Switch.Root>
              </div> */}
            </div>
          </div>

          {!isHaveNotification && !isDoNotDisturb && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <BellOff size={64} />
              You haven't received any notifications in the last{" "}
              {notifRetention} days
            </div>
          )}

          {isDoNotDisturb && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <BellOff size={64} />
              Notifications are not displayed in Do Not Disturb mode.
            </div>
          )}

          {/* Search & Sort */}
          {isHaveNotification && !isDoNotDisturb && (
            <div className="space-y-3 border-b border-border px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  Date Sort
                  <button
                    className="rounded p-0.5 transition-colors hover:bg-accent"
                    onClick={() =>
                      setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
                    }
                  >
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by keywords"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-border bg-gray-50 py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-axiata-blue focus:ring-opacity-20"
                />
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications &&
              isHaveNotification &&
              !isDoNotDisturb &&
              Object.entries(groupedNotifications || {})
                .sort(([dateA], [dateB]) => {
                  const timeA = new Date(dateA).getTime();
                  const timeB = new Date(dateB).getTime();
                  return sortOrder === "desc" ? timeB - timeA : timeA - timeB;
                })
                .map(([date, notifs]) => (
                  <div key={date}>
                    <div className="sticky top-0 z-10 bg-muted px-5 py-2 text-xs text-muted-foreground">
                      {formatDateString(date)}
                    </div>
                    <div className="divide-y divide-border">
                      {notifs.map((notif) => (
                        <div
                          key={notif.id}
                          className={`px-5 py-4 transition-colors hover:bg-accent/50 ${
                            !notif.isRead ? "bg-accent/30" : ""
                          } ${
                            notif.notification.actionUrl &&
                            notif.notification.type === "TODO"
                              ? "cursor-pointer hover:underline"
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              notif.notification.actionUrl &&
                              notif.notification.type === "TODO"
                            ) {
                              if (!notif.isRead) {
                                read({ id: notif.id });
                              }
                              navigate(
                                resolveActionUrl(notif.notification.actionUrl),
                              );
                              setOpen(false);
                            }
                          }}
                        >
                          <div className="flex gap-3">
                            {/* Icon */}
                            <div
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                notif.notification.type === "TODO"
                                  ? "bg-orange-100 text-orange-600"
                                  : "bg-blue-100 text-blue-600"
                              }`}
                            >
                              <div className="flex h-5 w-5 items-center justify-center">
                                {notif.notification.type === "TODO"
                                  ? "📋"
                                  : "📧"}
                              </div>
                            </div>

                            {/* Content */}
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-start justify-between gap-2">
                                <h4 className="line-clamp-1 text-sm text-foreground">
                                  {notif.notification.statusData} -{" "}
                                  {notif.notification.module} -{" "}
                                  {notif.notification.type}
                                </h4>
                                <DropdownMenu.Root>
                                  <DropdownMenu.Trigger asChild>
                                    <button
                                      className="shrink-0 rounded p-1 transition-colors hover:bg-background"
                                      onClick={(e) => e.stopPropagation()}
                                      onPointerDown={(e) => e.stopPropagation()}
                                    >
                                      <MoreVertical className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                  </DropdownMenu.Trigger>
                                  <DropdownMenu.Portal>
                                    <DropdownMenu.Content
                                      className="min-w-[160px] rounded-lg border border-border bg-popover p-1 shadow-lg"
                                      sideOffset={4}
                                    >
                                      <DropdownMenu.Item
                                        className="cursor-pointer rounded-md px-3 py-2 text-sm text-foreground outline-none hover:bg-accent"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleNotificationStatus(
                                            notif.id,
                                            notif.isRead,
                                          );
                                        }}
                                      >
                                        {notif.isRead
                                          ? "Mark as Unread"
                                          : "Mark as Read"}
                                      </DropdownMenu.Item>
                                    </DropdownMenu.Content>
                                  </DropdownMenu.Portal>
                                </DropdownMenu.Root>
                              </div>

                              <p className="mb-2 line-clamp-2 text-sm text-muted-foreground">
                                {notif.notification.message}
                              </p>

                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  on{" "}
                                  {formatIsoAsWib(
                                    notif.notification.createdDate,
                                  )}{" "}
                                </span>
                                {notif.isRead && (
                                  <Check className="h-3.5 w-3.5 text-green-600" />
                                )}
                                {!notif.isRead && (
                                  <Circle className="h-2 w-2 fill-blue-600 text-blue-600" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-5 py-3">
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => {
                navigate("/notifications");
                setOpen(false);
              }}
            >
              See All Notifications
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
